import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import baseURL from './baseUrl';

const DEFAULT_ROWS = 25;

const createEmptyRows = (count, startSrNo = 1) =>
  Array.from({ length: count }, (_, i) => ({
    srNo: startSrNo + i,
    weight: '',
    touch: ''
  }));

const formatReceiptDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
};

const rowHasEntry = (row) =>
  String(row.weight ?? '').trim() !== '' || String(row.touch ?? '').trim() !== '';

const balanceClassName = (balance) => {
  const value = parseFloat(balance);
  if (!Number.isFinite(value) || value === 0) return '';
  return value < 0 ? 'text-danger' : 'text-success';
};

const balanceMessage = (balance) => {
  const value = parseFloat(balance);
  if (!Number.isFinite(value) || value === 0) return 'Settled';
  if (value < 0) return 'Pure still needs to be given';
  return 'More pure was given overall';
};

const formatToGive = (balance) => {
  const value = parseFloat(balance);
  if (!Number.isFinite(value) || value >= 0) return '';
  return Math.abs(value).toFixed(3);
};

const formatBalanceDisplay = (balance) => {
  const value = parseFloat(balance);
  if (!Number.isFinite(value) || value <= 0) return '';
  return String(balance).trim() !== '' ? balance : value.toFixed(3);
};

const SpeedThane = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [receiptNo, setReceiptNo] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(() => createEmptyRows(DEFAULT_ROWS));
  const [pureGiven, setPureGiven] = useState('');
  const [savedReceipts, setSavedReceipts] = useState([]);
  const [completedTransactions, setCompletedTransactions] = useState([]);
  const [editingReceiptId, setEditingReceiptId] = useState(null);
  const [completeFromDate, setCompleteFromDate] = useState('');
  const [completeToDate, setCompleteToDate] = useState('');
  const [selectedReceiptIds, setSelectedReceiptIds] = useState([]);
  const inputRefs = useRef({});

  const fetchSavedReceipts = async () => {
    const response = await axios.get(`${baseURL}/api/speed-thane`);
    setSavedReceipts(response.data || []);
  };

  const fetchCompletedTransactions = async () => {
    const response = await axios.get(`${baseURL}/api/speed-thane/completions`);
    setCompletedTransactions(response.data || []);
  };

  const fetchNextReceiptNo = async () => {
    const response = await axios.get(`${baseURL}/api/speed-thane/next-receipt-no`);
    setReceiptNo(String(response.data?.nextReceiptNo || 1));
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        await Promise.all([fetchSavedReceipts(), fetchCompletedTransactions(), fetchNextReceiptNo()]);
      } catch (error) {
        window.alert('Failed to load Speed Thane receipts.');
      }
    };
    initialize();
  }, []);

  const computedRows = useMemo(
    () =>
      rows.map((row) => {
        const weightNum = parseFloat(row.weight);
        const touchNum = parseFloat(row.touch);
        const hasWeight = Number.isFinite(weightNum);
        const hasTouch = Number.isFinite(touchNum);

        if (!hasWeight || !hasTouch) {
          return { ...row, pure: '' };
        }

        const pure = (weightNum * touchNum) / 100;
        return { ...row, pure: pure.toFixed(3) };
      }),
    [rows]
  );

  const totals = useMemo(() => {
    let totalWeight = 0;
    let totalPure = 0;
    let hasWeightWithoutTouch = false;

    computedRows.forEach((row) => {
      const weightNum = parseFloat(row.weight);
      if (Number.isFinite(weightNum)) {
        totalWeight += weightNum;
        const touchNum = parseFloat(row.touch);
        if (!Number.isFinite(touchNum)) {
          hasWeightWithoutTouch = true;
        }
      }

      const pureNum = parseFloat(row.pure);
      if (Number.isFinite(pureNum)) totalPure += pureNum;
    });

    const totalPureValid = !hasWeightWithoutTouch && totalPure > 0;
    const pureValue = totalPureValid ? totalPure / 0.995 : null;

    const pureGivenNum = parseFloat(pureGiven);
    const hasPureGiven = Number.isFinite(pureGivenNum);
    const balanceValue =
      pureValue !== null && hasPureGiven ? pureGivenNum - pureValue : null;

    return {
      totalWeight: totalWeight ? totalWeight.toFixed(3) : '',
      totalPure: hasWeightWithoutTouch ? '' : totalPure ? totalPure.toFixed(3) : '',
      pure: pureValue !== null ? pureValue.toFixed(3) : '',
      pureGiven: hasPureGiven ? pureGivenNum.toFixed(3) : '',
      balance: balanceValue !== null ? balanceValue.toFixed(3) : ''
    };
  }, [computedRows, pureGiven]);

  const dateRangeReceipts = useMemo(() => {
    if (!completeFromDate || !completeToDate) {
      return [];
    }

    return savedReceipts
      .filter((receipt) => {
        const receiptDate = receipt.date || '';
        return receiptDate >= completeFromDate && receiptDate <= completeToDate;
      })
      .sort((a, b) => Number(a.receiptNo) - Number(b.receiptNo));
  }, [savedReceipts, completeFromDate, completeToDate]);

  const selectedCompleteReceipts = useMemo(
    () => dateRangeReceipts.filter((receipt) => selectedReceiptIds.includes(receipt._id)),
    [dateRangeReceipts, selectedReceiptIds]
  );

  const allDateRangeSelected =
    dateRangeReceipts.length > 0 &&
    dateRangeReceipts.every((receipt) => selectedReceiptIds.includes(receipt._id));

  const completePreviewTotal = useMemo(() => {
    const total = selectedCompleteReceipts.reduce((sum, receipt) => {
      const balanceNum = parseFloat(receipt.totals?.balance);
      return Number.isFinite(balanceNum) ? sum + balanceNum : sum;
    }, 0);

    return selectedCompleteReceipts.length > 0 ? total.toFixed(3) : '';
  }, [selectedCompleteReceipts]);

  const savedReceiptsTotals = useMemo(() => {
    let totalPositive = 0;
    let totalToGive = 0;

    savedReceipts.forEach((receipt) => {
      const value = parseFloat(receipt.totals?.balance);
      if (!Number.isFinite(value)) return;
      if (value > 0) totalPositive += value;
      if (value < 0) totalToGive += Math.abs(value);
    });

    const netBalance = totalPositive - totalToGive;
    const hasTotals = totalPositive > 0 || totalToGive > 0;

    return {
      totalPositive: totalPositive > 0 ? totalPositive.toFixed(3) : '',
      totalToGive: totalToGive > 0 ? totalToGive.toFixed(3) : '',
      netBalance: hasTotals ? netBalance.toFixed(3) : ''
    };
  }, [savedReceipts]);

  useEffect(() => {
    setSelectedReceiptIds([]);
  }, [completeFromDate, completeToDate]);

  const updateRowField = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addRows = (count) => {
    setRows((prev) => [...prev, ...createEmptyRows(count, prev.length + 1)]);
  };

  const resetDraft = async () => {
    setDate(new Date().toISOString().slice(0, 10));
    setRows(createEmptyRows(DEFAULT_ROWS));
    setPureGiven('');
    setEditingReceiptId(null);
    try {
      await fetchNextReceiptNo();
    } catch (error) {
      setReceiptNo('');
    }
  };

  const handleSaveReceipt = async () => {
    try {
      const payload = { date, rows, totals };
      if (editingReceiptId) {
        await axios.put(`${baseURL}/api/speed-thane/${editingReceiptId}`, payload);
      } else {
        await axios.post(`${baseURL}/api/speed-thane`, payload);
      }

      await fetchSavedReceipts();
      if (!editingReceiptId) {
        await fetchNextReceiptNo();
      }
      window.alert(editingReceiptId ? 'Receipt updated.' : 'Receipt saved.');
      const shouldPrint = window.confirm('Do you want to print this receipt now?');
      if (shouldPrint) {
        printPage();
      }
      setActiveTab('saved');
    } catch (error) {
      window.alert('Failed to save receipt.');
    }
  };

  const handleEditReceipt = (receipt) => {
    setReceiptNo(receipt.receiptNo || '');
    setDate(receipt.date || new Date().toISOString().slice(0, 10));
    setRows(
      Array.isArray(receipt.rows) && receipt.rows.length > 0
        ? receipt.rows.map((row, index) => ({
            srNo: index + 1,
            weight: row.weight ?? '',
            touch: row.touch ?? ''
          }))
        : createEmptyRows(DEFAULT_ROWS)
    );
    setPureGiven(receipt.totals?.pureGiven ?? '');
    setEditingReceiptId(receipt._id);
    setActiveTab('create');
  };

  const handleDeleteReceipt = async (receiptId) => {
    const ok = window.confirm('Delete this receipt?');
    if (!ok) return;
    try {
      await axios.delete(`${baseURL}/api/speed-thane/${receiptId}`);
      await fetchSavedReceipts();
      if (editingReceiptId === receiptId) {
        await resetDraft();
      }
    } catch (error) {
      window.alert('Failed to delete receipt.');
    }
  };

  const toggleReceiptSelection = (receiptId) => {
    setSelectedReceiptIds((prev) =>
      prev.includes(receiptId) ? prev.filter((id) => id !== receiptId) : [...prev, receiptId]
    );
  };

  const toggleSelectAllReceipts = () => {
    if (allDateRangeSelected) {
      setSelectedReceiptIds([]);
      return;
    }
    setSelectedReceiptIds(dateRangeReceipts.map((receipt) => receipt._id));
  };

  const handleCompleteTransaction = async () => {
    if (!completeFromDate || !completeToDate) {
      window.alert('Please select date range.');
      return;
    }

    if (selectedCompleteReceipts.length === 0) {
      window.alert('Please select at least one receipt to complete.');
      return;
    }

    const receiptNos = selectedCompleteReceipts.map((receipt) => receipt.receiptNo).join(', ');
    const ok = window.confirm(
      `Complete ${selectedCompleteReceipts.length} receipt(s)?\nReceipt Nos: ${receiptNos}\nFinal balance: ${completePreviewTotal} gms`
    );
    if (!ok) return;

    try {
      await axios.post(`${baseURL}/api/speed-thane/complete`, {
        fromDate: completeFromDate,
        toDate: completeToDate,
        receiptIds: selectedCompleteReceipts.map((receipt) => receipt._id)
      });

      await Promise.all([fetchSavedReceipts(), fetchCompletedTransactions()]);
      setCompleteFromDate('');
      setCompleteToDate('');
      setSelectedReceiptIds([]);
      window.alert('Transaction completed.');
      setActiveTab('complete');
    } catch (error) {
      window.alert(error.response?.data?.message || 'Failed to complete transaction.');
    }
  };

  const handlePrintSavedReceipt = (receipt) => {
    setReceiptNo(String(receipt.receiptNo || ''));
    setDate(receipt.date || new Date().toISOString().slice(0, 10));
    setRows(
      Array.isArray(receipt.rows) && receipt.rows.length > 0
        ? receipt.rows.map((row, index) => ({
            srNo: index + 1,
            weight: row.weight ?? '',
            touch: row.touch ?? ''
          }))
        : createEmptyRows(DEFAULT_ROWS)
    );
    setPureGiven(receipt.totals?.pureGiven ?? '');
    setEditingReceiptId(receipt._id);
    setActiveTab('create');

    // Wait for UI to render selected receipt before opening print dialog.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        printPage();
      });
    });
  };

  const focusCell = (rowIndex, field) => {
    window.requestAnimationFrame(() => {
      const target = inputRefs.current?.[rowIndex]?.[field];
      if (target) {
        target.focus();
        target.select?.();
      }
    });
  };

  const handleEnterNavigation = (e, rowIndex, field) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();

    if (field === 'weight') {
      focusCell(rowIndex, 'touch');
      return;
    }

    // From touch, move to next row's weight. Create a row if needed.
    const nextRowIndex = rowIndex + 1;
    if (nextRowIndex < rows.length) {
      focusCell(nextRowIndex, 'weight');
      return;
    }

    setRows((prev) => {
      const next = [...prev, ...createEmptyRows(1, prev.length + 1)];
      window.requestAnimationFrame(() => {
        const target = inputRefs.current?.[nextRowIndex]?.weight;
        if (target) {
          target.focus();
          target.select?.();
        }
      });
      return next;
    });
  };

  const printPage = () => {
    window.print();
  };

  const isFullWidthTab = activeTab === 'saved' || activeTab === 'complete';

  return (
    <div
      className={`${isFullWidthTab ? 'container-fluid' : 'container'} my-3 speed-thane-page${
        isFullWidthTab ? ' speed-thane-page--full' : ''
      }`}
    >
      <style>{`
        .speed-thane-page .sheet {
          background: #fff;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          padding: 12px;
        }
        .speed-thane-page .sheet-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 10px;
        }
        .speed-thane-page .sheet-title-block {
          flex: 1;
          min-width: 0;
        }
        .speed-thane-page .print-only {
          display: none;
        }
        .speed-thane-page .sheet-title {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 700;
        }
        .speed-thane-page .date-input-clean {
          border: none !important;
          box-shadow: none !important;
          background: transparent !important;
          padding-left: 0;
          padding-right: 0;
        }
        .speed-thane-page .date-input-clean:focus {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
        .speed-thane-page .entry-table input {
          border: none;
          outline: none;
          width: 100%;
          background: transparent;
          padding: 4px 6px;
        }
        .speed-thane-page .entry-table td,
        .speed-thane-page .entry-table th {
          vertical-align: middle;
          padding: 4px 6px;
          font-size: 13px;
        }
        .speed-thane-page .entry-table tfoot td {
          font-weight: 700;
          background: #f4f4f4;
        }
        .speed-thane-page .pure-display {
          min-height: 24px;
          display: inline-block;
          width: 100%;
        }
        .speed-thane-page .summary-block {
          margin-top: 8px;
          border: 1px solid #d9d9d9;
          border-radius: 6px;
          padding: 8px 12px;
          background: #fafafa;
          max-width: 360px;
          margin-left: auto;
        }
        .speed-thane-page .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 4px 0;
          font-size: 13px;
        }
        .speed-thane-page .summary-row + .summary-row {
          border-top: 1px dashed #e0e0e0;
        }
        .speed-thane-page .summary-label {
          font-weight: 600;
          color: #333;
        }
        .speed-thane-page .summary-value {
          font-weight: 700;
          text-align: right;
          min-width: 120px;
        }
        .speed-thane-page .summary-input {
          width: 140px;
          text-align: right;
        }
        .speed-thane-page .complete-form {
          border: 1px solid #d9d9d9;
          border-radius: 6px;
          padding: 12px;
          background: #fafafa;
        }
        .speed-thane-page--full {
          max-width: 100%;
          padding-left: 16px;
          padding-right: 16px;
        }
        .speed-thane-page--full .sheet {
          width: 100%;
        }
        .speed-thane-page--full .table-responsive {
          width: 100%;
        }
        .speed-thane-page .saved-receipts-table {
          width: 100%;
          table-layout: auto;
        }
        .speed-thane-page .saved-receipts-table .col-balance,
        .speed-thane-page .saved-receipts-table .col-to-give {
          min-width: 120px;
          white-space: nowrap;
        }
        .speed-thane-page .saved-receipts-table .col-actions {
          min-width: 280px;
          white-space: nowrap;
        }
        .speed-thane-page .saved-receipts-table tfoot td {
          font-weight: 700;
          background: #f4f4f4;
        }
        .speed-thane-page .saved-receipts-table tfoot .saved-receipts-net-row td {
          background: #e8f4e8;
        }
        @media print {
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          .navbar,
          .speed-thane-controls,
          .no-print {
            display: none !important;
          }
          .speed-thane-page .print-only {
            display: block !important;
          }
          .speed-thane-page tr.speed-thane-row-empty-print {
            display: none !important;
          }
          .container.speed-thane-page,
          .speed-thane-page.container {
            max-width: 100% !important;
          }
          .speed-thane-page.my-3 {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          .speed-thane-page {
            width: 148mm !important;
            min-height: 0 !important;
            margin: 0 auto !important;
            padding: 2mm 4mm 4mm !important;
          }
          .speed-thane-page .sheet {
            border: none !important;
            padding: 0 !important;
            border-radius: 0 !important;
          }
          .speed-thane-page .sheet-header {
            margin-bottom: 6px !important;
          }
          .speed-thane-page .entry-table td,
          .speed-thane-page .entry-table th {
            font-size: 11px !important;
            padding: 2px 4px !important;
          }
          @page {
            size: A5 portrait;
            margin: 2mm 4mm 5mm;
          }
        }
      `}</style>

      <div className="mb-3 no-print">
        <div className="btn-group" role="group" aria-label="Speed Thane tabs">
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'create' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('create')}
          >
            Create Receipt
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'saved' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('saved')}
          >
            Saved Receipts ({savedReceipts.length})
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'complete' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('complete')}
          >
            Complete ({completedTransactions.length})
          </button>
        </div>
      </div>

      {activeTab === 'create' ? (
      <div className="sheet">
        <div className="sheet-header">
          <div className="sheet-title-block">
            <h2 className="sheet-title">Speed Thane</h2>
            <div className="mt-1 no-print">
              <label className="form-label mb-0 small text-muted">Date</label>
              <input
                type="date"
                className="form-control form-control-sm date-input-clean"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="print-only mt-1 fw-semibold" style={{ fontSize: '0.95rem' }}>
              {formatReceiptDate(date)}
            </div>
          </div>
          <div className="d-flex align-items-start gap-2" style={{ minWidth: 180 }}>
            <button
              type="button"
              className="btn btn-success btn-sm no-print"
              onClick={handleSaveReceipt}
            >
              Print Receipt
            </button>
            <div className="d-flex flex-column gap-1 text-end" style={{ minWidth: 140 }}>
              <div className="fw-semibold">Receipt No : {receiptNo || '-'}</div>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered entry-table mb-2">
            <thead>
              <tr>
                <th style={{ width: 70 }}>Sr No</th>
                <th style={{ width: 180 }}>Weight</th>
                <th style={{ width: 180 }}>Touch</th>
                <th style={{ width: 180 }}>Pure</th>
              </tr>
            </thead>
            <tbody>
              {computedRows.map((row, index) => (
                <tr
                  key={row.srNo}
                  className={rowHasEntry(row) ? '' : 'speed-thane-row-empty-print'}
                >
                  <td>{row.srNo}</td>
                  <td>
                    <input
                      type="number"
                      step="0.001"
                      value={row.weight}
                      ref={(el) => {
                        if (!inputRefs.current[index]) inputRefs.current[index] = {};
                        inputRefs.current[index].weight = el;
                      }}
                      onChange={(e) => updateRowField(index, 'weight', e.target.value)}
                      onKeyDown={(e) => handleEnterNavigation(e, index, 'weight')}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={row.touch}
                      ref={(el) => {
                        if (!inputRefs.current[index]) inputRefs.current[index] = {};
                        inputRefs.current[index].touch = el;
                      }}
                      onChange={(e) => updateRowField(index, 'touch', e.target.value)}
                      onKeyDown={(e) => handleEnterNavigation(e, index, 'touch')}
                    />
                  </td>
                  <td>
                    <span className="pure-display">{row.pure}</span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td>{totals.totalWeight}</td>
                <td></td>
                <td>{totals.totalPure}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="summary-block">
          <div className="summary-row">
            <span className="summary-label">Total Pure</span>
            <span className="summary-value">{totals.totalPure || '-'} gms</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Pure (÷ 0.995)</span>
            <span className="summary-value">{totals.pure || '-'} gms</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Pure Given</span>
            <span className="summary-value">
              <input
                type="number"
                step="0.001"
                className="form-control form-control-sm summary-input no-print"
                value={pureGiven}
                onChange={(e) => setPureGiven(e.target.value)}
                placeholder="Enter pure given"
              />
              <span className="print-only">{totals.pureGiven || '-'} gms</span>
            </span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Balance</span>
            <span
              className={`summary-value ${balanceClassName(totals.balance)}`}
            >
              {totals.balance !== '' ? `${totals.balance} gms` : '-'}
            </span>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 speed-thane-controls no-print">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveReceipt}>
            {editingReceiptId ? 'Update Receipt' : 'Save Receipt'}
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetDraft}>
            New Receipt
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRows(1)}>
            Add 1 Row
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRows(5)}>
            Add 5 Rows
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRows(10)}>
            Add 10 Rows
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addRows(25)}>
            Add 25 Rows
          </button>
          <button type="button" className="btn btn-success btn-sm ms-auto" onClick={handleSaveReceipt}>
            Print Receipt
          </button>
        </div>
      </div>
      ) : activeTab === 'saved' ? (
      <div className="sheet sheet-full">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="sheet-title mb-0">Saved Receipts</h2>
        </div>
        <div className="table-responsive saved-receipts-wrap">
          <table className="table table-bordered table-sm mb-0 saved-receipts-table">
            <thead>
              <tr>
                <th>Receipt No</th>
                <th>Date</th>
                <th>Total Weight</th>
                <th>Total Pure</th>
                <th>Pure</th>
                <th>Pure Given</th>
                <th className="col-balance">Balance</th>
                <th className="col-to-give">To Give</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {savedReceipts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-3">
                    No receipts saved yet.
                  </td>
                </tr>
              ) : (
                savedReceipts.map((receipt) => (
                  <tr key={receipt._id}>
                    <td>{receipt.receiptNo}</td>
                    <td>{receipt.date}</td>
                    <td>{receipt.totals?.totalWeight || ''}</td>
                    <td>{receipt.totals?.totalPure || ''}</td>
                    <td>{receipt.totals?.pure || ''}</td>
                    <td>{receipt.totals?.pureGiven || ''}</td>
                    <td className={`col-balance ${balanceClassName(receipt.totals?.balance)}`}>
                      {formatBalanceDisplay(receipt.totals?.balance)}
                    </td>
                    <td className="col-to-give text-danger fw-semibold">
                      {formatToGive(receipt.totals?.balance)}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          onClick={() => handlePrintSavedReceipt(receipt)}
                        >
                          Print
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleEditReceipt(receipt)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDeleteReceipt(receipt._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {savedReceipts.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={6} className="text-end">
                    Total
                  </td>
                  <td className={`col-balance text-success ${balanceClassName(savedReceiptsTotals.totalPositive)}`}>
                    {savedReceiptsTotals.totalPositive}
                  </td>
                  <td className="col-to-give text-danger">
                    {savedReceiptsTotals.totalToGive}
                  </td>
                  <td className="col-actions"></td>
                </tr>
                <tr className="saved-receipts-net-row">
                  <td colSpan={6} className="text-end">
                    Balance
                  </td>
                  <td className={`col-balance ${balanceClassName(savedReceiptsTotals.netBalance)}`}>
                    {savedReceiptsTotals.netBalance}
                  </td>
                  <td className="col-to-give"></td>
                  <td className="col-actions"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      ) : (
      <div className="sheet sheet-full">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="sheet-title mb-0">Complete Transaction</h2>
        </div>

        <div className="complete-form mb-3">
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label small mb-1">From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={completeFromDate}
                onChange={(e) => setCompleteFromDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-1">To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={completeToDate}
                onChange={(e) => setCompleteToDate(e.target.value)}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={toggleSelectAllReceipts}
                disabled={dateRangeReceipts.length === 0}
              >
                {allDateRangeSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        </div>

        <div className="table-responsive mb-3">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr>
                <th style={{ width: 48 }}>
                  <input
                    type="checkbox"
                    checked={allDateRangeSelected}
                    onChange={toggleSelectAllReceipts}
                    disabled={dateRangeReceipts.length === 0}
                    aria-label="Select all receipts"
                  />
                </th>
                <th>Receipt No</th>
                <th>Date</th>
                <th>Pure Given</th>
                <th>Pure</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {dateRangeReceipts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">
                    Select date range to show receipts.
                  </td>
                </tr>
              ) : (
                dateRangeReceipts.map((receipt) => {
                  const isSelected = selectedReceiptIds.includes(receipt._id);
                  return (
                    <tr key={receipt._id} className={isSelected ? 'table-primary' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleReceiptSelection(receipt._id)}
                          aria-label={`Select receipt ${receipt.receiptNo}`}
                        />
                      </td>
                      <td>{receipt.receiptNo}</td>
                      <td>{receipt.date}</td>
                      <td>{receipt.totals?.pureGiven || ''}</td>
                      <td>{receipt.totals?.pure || ''}</td>
                      <td className={balanceClassName(receipt.totals?.balance)}>
                        {receipt.totals?.balance || ''}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {selectedCompleteReceipts.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={5} className="text-end fw-bold">
                    Final Balance ({selectedCompleteReceipts.length} selected)
                  </td>
                  <td className={`fw-bold ${balanceClassName(completePreviewTotal)}`}>
                    {completePreviewTotal} gms
                    <div className="small fw-normal">{balanceMessage(completePreviewTotal)}</div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="mb-4">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleCompleteTransaction}
            disabled={selectedCompleteReceipts.length === 0}
          >
            Complete Transaction
          </button>
        </div>

        <h3 className="sheet-title h5 mb-2">Completed Transactions</h3>
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr>
                <th>Complete No</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>From Receipt</th>
                <th>To Receipt</th>
                <th>Receipts</th>
                <th>Final Balance</th>
              </tr>
            </thead>
            <tbody>
              {completedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-3">
                    No completed transactions yet.
                  </td>
                </tr>
              ) : (
                completedTransactions.map((item) => (
                  <tr key={item._id}>
                    <td>{item.completeNo}</td>
                    <td>{item.fromDate}</td>
                    <td>{item.toDate}</td>
                    <td>{item.fromReceiptNo}</td>
                    <td>{item.toReceiptNo}</td>
                    <td>{item.receipts?.length || 0}</td>
                    <td className={balanceClassName(item.finalBalance)}>
                      {item.finalBalance} gms
                      <div className="small fw-normal">{balanceMessage(item.finalBalance)}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default SpeedThane;
